import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Plus, Search, FileText, Truck, Download, Eye, Loader2, Trash2,
  ChevronLeft, ChevronRight, ArrowRight, MoreHorizontal, Pencil, Printer, Link2, Mail, Lock, PenLine, ShieldCheck
} from 'lucide-react';
import { SignLinkDialog, EmailDialog, FinalizeDialog } from '../components/DocumentActions';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';

const fmt = (v) => new Intl.NumberFormat('fr-DZ', { maximumFractionDigits: 0 }).format(v || 0) + ' DZD';
const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return '-'; } };

export default function DocumentsPage() {
  const [tab, setTab] = useState('facture');

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Documents</h1>
        <p className="text-sm text-slate-500">Factures, bons de livraison et proforma</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="facture" className="data-[state=active]:bg-white data-[state=active]:text-[#0A3D73]" data-testid="tab-factures">
            <FileText className="w-4 h-4 mr-1.5" /> Factures
          </TabsTrigger>
          <TabsTrigger value="proforma" className="data-[state=active]:bg-white data-[state=active]:text-[#0A3D73]" data-testid="tab-proforma">
            <FileText className="w-4 h-4 mr-1.5" /> Proforma
          </TabsTrigger>
          <TabsTrigger value="bl" className="data-[state=active]:bg-white data-[state=active]:text-[#0A3D73]" data-testid="tab-bl">
            <Truck className="w-4 h-4 mr-1.5" /> Bons de livraison
          </TabsTrigger>
        </TabsList>
        <TabsContent value="facture" className="mt-4"><DocList docType="facture" /></TabsContent>
        <TabsContent value="proforma" className="mt-4"><DocList docType="proforma" /></TabsContent>
        <TabsContent value="bl" className="mt-4"><DocList docType="bl" /></TabsContent>
      </Tabs>
    </div>
  );
}

function DocList({ docType }) {
  const label = docType === 'facture' ? 'Facture' : docType === 'proforma' ? 'Proforma' : 'Bon de livraison';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [signLinkDoc, setSignLinkDoc] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);
  const [finalizeDoc, setFinalizeDoc] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = (text, ok = true) => { setToast({ text, ok }); setTimeout(() => setToast(null), 4000); };

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { doc_type: docType, page, limit: 50 };
      if (search) params.search = search;
      const { data } = await api.get('/documents', { params });
      setItems(data.items); setTotal(data.total); setPages(data.pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [docType, page, search]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300); return () => clearTimeout(t); }, [searchInput]);

  // inline => le PDF s'ouvre dans le visualiseur du navigateur (bouton imprimer accessible) ; download=1 => fichier
  const pdfUrl = (docId, download = false) => {
    const token = localStorage.getItem('access_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/pdf?token=${token}${download ? '&download=1' : ''}`;
  };
  const openPdf = (docId) => window.open(pdfUrl(docId), '_blank');
  const downloadPdf = (docId) => { const a = document.createElement('a'); a.href = pdfUrl(docId, true); a.download = ''; document.body.appendChild(a); a.click(); a.remove(); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await api.delete(`/documents/${deleteTarget._id}`); setDeleteTarget(null); fetch(); }
    catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={`Rechercher par n° ou client...`} className="pl-10 border-slate-300" data-testid={`${docType}-search`} />
        </div>
        <Button onClick={() => { setEditDoc(null); setFormOpen(true); }} className="bg-[#0A3D73] hover:bg-[#082E56] text-white" data-testid={`new-${docType}-btn`}>
          <Plus className="w-4 h-4 mr-2" /> {label}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#0A3D73]" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 mx-auto bg-slate-100 rounded-md flex items-center justify-center mb-4">
              {docType === 'facture' ? <FileText className="w-7 h-7 text-slate-400" /> : <Truck className="w-7 h-7 text-slate-400" />}
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">{search ? 'Aucun résultat' : `Aucun ${label.toLowerCase()}`}</h3>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider">N°</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Date</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Client</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider hidden md:table-cell">Articles</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider text-right">Total</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d._id} className="hover:bg-slate-50/50" data-testid={`doc-row-${d._id}`}>
                    <TableCell className="font-mono text-sm font-semibold text-[#0A3D73]">
                      <div className="flex items-center gap-2">
                        {d.doc_number}
                        {d.client_signature ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 text-[10px] font-sans px-1.5 py-0" data-testid={`badge-signed-${d._id}`}><ShieldCheck className="w-3 h-3 mr-1" />Signé</Badge>
                        ) : d.pdf_frozen ? (
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100 text-[10px] font-sans px-1.5 py-0" data-testid={`badge-frozen-${d._id}`}><Lock className="w-3 h-3 mr-1" />Finalisé</Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDate(d.created_at)}</TableCell>
                    <TableCell className="font-medium text-slate-900">{d.client_name}</TableCell>
                    <TableCell className="text-sm text-slate-500 hidden md:table-cell">{(d.items || []).length} article{(d.items || []).length > 1 ? 's' : ''}</TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">{fmt(d.total)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`doc-menu-${d._id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                          <DropdownMenuItem onClick={() => setPreviewDoc(d)}><Eye className="w-4 h-4 mr-2" /> Aperçu</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openPdf(d._id)} data-testid={`doc-print-${d._id}`}><Printer className="w-4 h-4 mr-2" /> Voir / Imprimer le PDF</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadPdf(d._id)} data-testid={`doc-download-${d._id}`}><Download className="w-4 h-4 mr-2" /> Télécharger le PDF</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEmailDoc(d)} data-testid={`doc-email-${d._id}`}><Mail className="w-4 h-4 mr-2" /> Envoyer par email</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {!d.client_signature && (
                            <DropdownMenuItem onClick={() => setSignLinkDoc(d)} className="text-[#0A3D73]" data-testid={`doc-signlink-${d._id}`}><PenLine className="w-4 h-4 mr-2" /> Faire signer par le client</DropdownMenuItem>
                          )}
                          {!d.pdf_frozen && (
                            <>
                              <DropdownMenuItem onClick={() => setFinalizeDoc(d)} data-testid={`doc-finalize-${d._id}`}><Lock className="w-4 h-4 mr-2" /> Finaliser (figer le PDF)</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setEditDoc(d); setFormOpen(true); }} data-testid={`doc-edit-${d._id}`}><Pencil className="w-4 h-4 mr-2" /> Modifier</DropdownMenuItem>
                            </>
                          )}
                          {docType === 'proforma' && !d.converted_to && (
                            <DropdownMenuItem onClick={async () => { try { await api.post(`/documents/${d._id}/convert`); fetch(); } catch(e) { console.error(e); } }} className="text-emerald-600">
                              <ArrowRight className="w-4 h-4 mr-2" /> Convertir en facture
                            </DropdownMenuItem>
                          )}
                          {!d.pdf_frozen && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setDeleteTarget(d)} className="text-red-600" data-testid={`doc-delete-${d._id}`}><Trash2 className="w-4 h-4 mr-2" /> Supprimer</DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-sm text-slate-500">Page {page} sur {pages}</p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-slate-300 h-8"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-slate-300 h-8"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>

      <DocForm open={formOpen} docType={docType} doc={editDoc} onClose={() => { setFormOpen(false); setEditDoc(null); }} onSaved={fetch} />
      <DocPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} onOpenPdf={openPdf} onDownload={downloadPdf} />
      <SignLinkDialog doc={signLinkDoc} onClose={() => setSignLinkDoc(null)} />
      <EmailDialog doc={emailDoc} onClose={() => setEmailDoc(null)} onSent={(m) => { notify(m); fetch(); }} />
      <FinalizeDialog doc={finalizeDoc} onClose={() => setFinalizeDoc(null)} onDone={() => { notify('Document finalisé et scellé'); fetch(); }} />
      {toast && (
        <div className={`fixed bottom-5 right-5 z-[100] px-4 py-3 rounded-md shadow-lg text-sm font-medium border animate-in slide-in-from-bottom-2 ${toast.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="doc-toast">{toast.text}</div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Supprimer {deleteTarget?.doc_number} ?</AlertDialogTitle>
            <AlertDialogDescription>Ce document sera supprimé définitivement.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-300">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============ Document Form ============
function DocForm({ open, docType, doc, onClose, onSaved }) {
  const isEdit = !!doc;
  const label = docType === 'facture' ? 'Facture' : docType === 'proforma' ? 'Proforma' : 'Bon de livraison';
  const emptyForm = { client_name: '', client_phone: '', client_email: '', client_address: '', client_rc: '', client_nif: '', client_nis: '', client_art: '', client_rib_bna: '', items: [{ reference: '', name: '', quantity: 1, unit_price: 0 }], discount: 0, tva: '', timbre_apply: true, timbre_manual: null, payment_method: '', notes: '', apply_stamp: false, apply_signature: false };
  const [form, setForm] = useState(emptyForm);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && doc) {
      setForm({
        client_name: doc.client_name || '', client_phone: doc.client_phone || '',
        client_email: doc.client_email || '', client_address: doc.client_address || '',
        client_rc: doc.client_rc || '', client_nif: doc.client_nif || '',
        client_nis: doc.client_nis || '', client_art: doc.client_art || '',
        client_rib_bna: doc.client_rib_bna || '',
        items: (doc.items || []).length > 0 ? doc.items.map(i => ({ ...i })) : [{ reference: '', name: '', quantity: 1, unit_price: 0 }],
        discount: doc.discount || 0, tva: doc.tva || '',
        timbre_apply: doc.timbre_apply !== undefined ? doc.timbre_apply : true,
        timbre_manual: doc.timbre !== undefined && doc.timbre !== null ? doc.timbre : null,
        payment_method: doc.payment_method || '', notes: doc.notes || '',
        apply_stamp: !!doc.apply_stamp, apply_signature: !!doc.apply_signature,
      });
    } else if (open) {
      setForm(emptyForm);
    }
    setError(''); setClientSearch(''); setClientResults([]);
  }, [open, doc]);

  // Client search
  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClientResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get('/clients', { params: { search: clientSearch, limit: 5 } }); setClientResults(data.items || []); }
      catch { setClientResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const selectClient = (c) => {
    setForm(p => ({ ...p, client_name: c.name || '', client_phone: c.phone || '', client_email: c.email || '', client_address: c.address || '', client_rc: c.rc || '', client_nif: c.nif || '', client_nis: c.nis || '', client_art: c.art || '', client_rib_bna: c.rib_bna || '' }));
    setClientSearch(''); setClientResults([]);
  };

  useEffect(() => {
    if (!productSearch || productSearch.length < 2) { setProductResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get('/products', { params: { search: productSearch, limit: 6 } }); setProductResults(data.products); }
      catch { setProductResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [productSearch]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setItem = (idx, k, v) => {
    const n = [...form.items]; n[idx] = { ...n[idx], [k]: v };
    // Any line edit clears the manual timbre override so auto 1% resumes
    setForm(p => ({ ...p, items: n, timbre_manual: null }));
  };
  const addItem = (p) => {
    const productName = (p.name && p.name !== p.reference) ? p.name : p.brand ? `${p.reference} ${p.brand}` : p.reference;
    setForm(prev => ({ ...prev, items: [...prev.items.filter(i => i.reference), { reference: p.reference, name: productName, quantity: 1, unit_price: p.sale_price || 0 }], timbre_manual: null }));
    setProductSearch(''); setProductResults([]);
  };
  const addBlankLine = () => setForm(p => ({ ...p, items: [...p.items, { reference: '', name: '', quantity: 1, unit_price: 0 }], timbre_manual: null }));
  const removeLine = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx), timbre_manual: null }));

  const subtotal = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
  const netHT = subtotal - (form.discount || 0);
  const tvaAmount = form.tva ? netHT * parseFloat(form.tva) / 100 : 0;
  const timbreAuto = Math.round(netHT * 0.01 * 100) / 100;
  const timbreValue = form.timbre_apply ? (form.timbre_manual !== null && form.timbre_manual !== undefined ? form.timbre_manual : timbreAuto) : 0;
  const total = netHT + tvaAmount + timbreValue;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) { setError('Client obligatoire'); return; }
    if (!form.items.some(i => i.reference)) { setError('Ajoutez au moins un article'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        items: form.items.filter(i => i.reference),
        timbre_apply: form.timbre_apply,
        timbre: form.timbre_apply ? timbreValue : 0,
      };
      // Backend also does discount recompute; strip the transient UI-only field
      delete payload.timbre_manual;
      if (isEdit) {
        await api.put(`/documents/${doc._id}`, payload);
      } else {
        await api.post('/documents', { ...payload, doc_type: docType });
      }
      onSaved(); onClose();
    } catch (err) { setError(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${docType}-form-dialog`}>
        <DialogHeader><DialogTitle>{isEdit ? `Modifier ${label}` : `Nouveau ${label}`}</DialogTitle></DialogHeader>
        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Rechercher un client</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Tapez un nom de client..." className="pl-10 border-slate-300" data-testid="doc-client-search" />
              {clientResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg z-50 mt-1 max-h-40 overflow-y-auto">
                  {clientResults.map(c => (
                    <button key={c.id} type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0" onClick={() => selectClient(c)}>
                      <span className="font-medium">{c.name}</span>{c.phone && <span className="text-slate-400 ml-2">{c.phone}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Nom du client *</Label><Input value={form.client_name} onChange={(e) => set('client_name', e.target.value)} className="border-slate-300" data-testid="doc-client-name" /></div>
            <div className="space-y-1.5"><Label>Téléphone</Label><Input value={form.client_phone} onChange={(e) => set('client_phone', e.target.value)} className="border-slate-300" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.client_email} onChange={(e) => set('client_email', e.target.value)} className="border-slate-300" /></div>
            <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.client_address} onChange={(e) => set('client_address', e.target.value)} className="border-slate-300" /></div>
          </div>
          {/* Client fiscal info (read-only when auto-filled) */}
          {(form.client_rc || form.client_nif || form.client_nis || form.client_art || form.client_rib_bna) && (
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-[#0A3D73] uppercase mb-2">Informations fiscales du client</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {form.client_rc && <div><span className="text-xs text-slate-500">RC</span><p className="text-sm font-medium text-slate-900 bg-slate-50 rounded px-2 py-1">{form.client_rc}</p></div>}
                {form.client_nif && <div><span className="text-xs text-slate-500">NIF</span><p className="text-sm font-medium text-slate-900 bg-slate-50 rounded px-2 py-1">{form.client_nif}</p></div>}
                {form.client_nis && <div><span className="text-xs text-slate-500">NIS</span><p className="text-sm font-medium text-slate-900 bg-slate-50 rounded px-2 py-1">{form.client_nis}</p></div>}
                {form.client_art && <div><span className="text-xs text-slate-500">ART</span><p className="text-sm font-medium text-slate-900 bg-slate-50 rounded px-2 py-1">{form.client_art}</p></div>}
                {form.client_rib_bna && <div className="col-span-2"><span className="text-xs text-slate-500">RIB BNA</span><p className="text-sm font-medium text-slate-900 bg-slate-50 rounded px-2 py-1">{form.client_rib_bna}</p></div>}
              </div>
            </div>
          )}

          {/* Product search */}
          <div className="space-y-2">
            <Label>Ajouter un article</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Rechercher par référence..." className="pl-10 border-slate-300" data-testid="doc-product-search" />
              {productResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg z-50 mt-1 max-h-40 overflow-y-auto">
                  {productResults.map(p => (
                    <button key={p.id} type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 flex justify-between" onClick={() => addItem(p)}>
                      <span className="font-mono text-[#0A3D73] font-medium">{p.reference}</span>
                      <span className="text-slate-400">{p.brand} <span className={`text-xs px-1 rounded ${p.state === 'neuf' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{p.state === 'neuf' ? 'N' : 'O'}</span> | {fmt(p.sale_price)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          {form.items.length > 0 && (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="text-xs w-36">Référence</TableHead>
                  <TableHead className="text-xs">Désignation</TableHead>
                  <TableHead className="text-xs w-20">Qté</TableHead>
                  <TableHead className="text-xs w-28">Prix unit.</TableHead>
                  <TableHead className="text-xs w-24 text-right">Total</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {form.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell><Input value={item.reference} onChange={(e) => setItem(idx, 'reference', e.target.value)} className="h-7 text-sm border-slate-300" /></TableCell>
                      <TableCell><Input value={item.name} onChange={(e) => setItem(idx, 'name', e.target.value)} className="h-7 text-sm border-slate-300" /></TableCell>
                      <TableCell><Input type="number" min="1" value={item.quantity} onChange={(e) => setItem(idx, 'quantity', parseInt(e.target.value) || 1)} className="h-7 text-sm border-slate-300 w-16" /></TableCell>
                      <TableCell><Input type="number" min="0" step="1" value={item.unit_price} onChange={(e) => setItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} className="h-7 text-sm border-slate-300 w-24" /></TableCell>
                      <TableCell className="text-right text-sm font-medium">{fmt((item.quantity || 0) * (item.unit_price || 0))}</TableCell>
                      <TableCell><button type="button" onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-600">x</button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Button type="button" variant="outline" size="sm" onClick={addBlankLine} className="border-slate-300 text-sm"><Plus className="w-3.5 h-3.5 mr-1" /> Ligne</Button>

          <div className="flex justify-end">
            <div className="w-64 space-y-2 border-t pt-3">
              <div className="flex justify-between text-sm"><span>Sous-total HT</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between text-sm items-center"><span>Remise</span><Input type="number" min="0" value={form.discount} onChange={(e) => set('discount', parseFloat(e.target.value) || 0)} className="w-24 h-7 text-sm text-right border-slate-300" data-testid="doc-discount" /></div>
              <div className="flex justify-between text-sm items-center"><span>TVA %</span><Input type="number" min="0" max="100" value={form.tva} onChange={(e) => set('tva', e.target.value)} placeholder="Ex: 19" className="w-24 h-7 text-sm text-right border-slate-300" data-testid="doc-tva" /></div>
              {form.tva && parseFloat(form.tva) > 0 && <div className="flex justify-between text-sm"><span>TVA {form.tva}%</span><span>{fmt(tvaAmount)}</span></div>}
              {/* Timbre fiscal 1% */}
              <div className="pt-1.5 border-t border-slate-100">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.timbre_apply}
                    onChange={(e) => setForm(p => ({ ...p, timbre_apply: e.target.checked, timbre_manual: null }))}
                    className="w-3.5 h-3.5 rounded border-slate-300 text-[#0A3D73] focus:ring-[#0A3D73]"
                    data-testid="doc-timbre-checkbox"
                  />
                  <span className="text-slate-700">Appliquer le timbre 1%</span>
                </label>
                <div className="flex justify-between text-sm items-center mt-1.5">
                  <span className={form.timbre_apply ? '' : 'text-slate-400'}>Timbre</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.timbre_apply ? timbreValue : 0}
                    onChange={(e) => setForm(p => ({ ...p, timbre_manual: parseFloat(e.target.value) || 0 }))}
                    disabled={!form.timbre_apply}
                    className="w-24 h-7 text-sm text-right border-slate-300 disabled:bg-slate-50 disabled:text-slate-400"
                    data-testid="doc-timbre-input"
                  />
                </div>
              </div>
              <div className="flex justify-between font-bold text-base border-t pt-2"><span>NET À PAYER</span><span className="text-[#0A3D73]" data-testid="doc-total">{fmt(total)}</span></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Modalité de paiement</Label>
              <Select value={form.payment_method} onValueChange={(v) => set('payment_method', v)}>
                <SelectTrigger className="border-slate-300"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Espèces">Espèces</SelectItem>
                  <SelectItem value="Virement">Virement</SelectItem>
                  <SelectItem value="Chèque">Chèque</SelectItem>
                  <SelectItem value="Crédit">Crédit</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5"><Label>Notes</Label><textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3D73]/50 resize-none" /></div>

          {/* Cachet & signature entreprise (images définies dans Paramètres > Entreprise) */}
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60">
            <p className="text-xs font-semibold text-[#0A3D73] uppercase mb-2">Cachet et signature sur le PDF</p>
            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={form.apply_stamp} onChange={(e) => set('apply_stamp', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-[#0A3D73] focus:ring-[#0A3D73]" data-testid="doc-apply-stamp" />
                <span className="text-slate-700">Apposer le cachet</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={form.apply_signature} onChange={(e) => set('apply_signature', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-[#0A3D73] focus:ring-[#0A3D73]" data-testid="doc-apply-signature" />
                <span className="text-slate-700">Apposer la signature</span>
              </label>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Les images se configurent dans Paramètres → Entreprise. Sans image enregistrée, la case n'a aucun effet.</p>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-300">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#0A3D73] hover:bg-[#082E56] text-white" data-testid={`${docType}-form-submit`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{isEdit ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ Document Preview ============
function DocPreview({ doc, onClose, onOpenPdf, onDownload }) {
  if (!doc) return null;
  const isFacture = doc.doc_type === 'facture';
  const isProforma = doc.doc_type === 'proforma';
  const items = doc.items || [];
  const subtotal = items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="doc-preview-dialog">
        {/* Preview header */}
        <div className="bg-[#0A3D73] text-white p-6 rounded-t-md -mx-6 -mt-6 mb-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-bold">{isProforma ? 'FACTURE PROFORMA' : isFacture ? 'FACTURE' : 'BON DE LIVRAISON'}</h2>
              <p className="text-white/70 text-sm mt-1">N° {doc.doc_number}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-white/70">Date</p>
              <p className="font-medium">{fmtDate(doc.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Client */}
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Client</p>
          <p className="font-semibold text-slate-900">{doc.client_name}</p>
          {doc.client_phone && <p className="text-sm text-slate-600">Tél: {doc.client_phone}</p>}
          {doc.client_email && <p className="text-sm text-slate-600">{doc.client_email}</p>}
          {doc.client_address && <p className="text-sm text-slate-600">{doc.client_address}</p>}
        </div>

        {/* Items */}
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="text-xs">#</TableHead>
            <TableHead className="text-xs">Référence</TableHead>
            <TableHead className="text-xs">Désignation</TableHead>
            <TableHead className="text-xs text-center">Qté</TableHead>
            <TableHead className="text-xs text-right">Prix Unit.</TableHead>
            <TableHead className="text-xs text-right">Total</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell className="text-sm">{idx + 1}</TableCell>
                <TableCell className="font-mono text-sm text-[#0A3D73] font-medium">{item.reference}</TableCell>
                <TableCell className="text-sm">{item.name || item.description}</TableCell>
                <TableCell className="text-sm text-center">{item.quantity}</TableCell>
                <TableCell className="text-sm text-right">{fmt(item.unit_price)}</TableCell>
                <TableCell className="text-sm text-right font-medium">{fmt((item.quantity || 0) * (item.unit_price || 0))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Totals */}
        <div className="flex justify-end mt-4">
          <div className="w-60 space-y-2">
            <div className="flex justify-between text-sm"><span>Sous-total HT</span><span>{fmt(subtotal)}</span></div>
            {doc.discount > 0 && <div className="flex justify-between text-sm"><span>Remise</span><span>-{fmt(doc.discount)}</span></div>}
            {doc.tva > 0 && <div className="flex justify-between text-sm"><span>TVA {doc.tva}%</span><span>{fmt(doc.tva_amount != null ? doc.tva_amount : (subtotal - (doc.discount || 0)) * doc.tva / 100)}</span></div>}
            {doc.timbre_apply && (doc.timbre || 0) > 0 && (
              <div className="flex justify-between text-sm"><span>Timbre 1%</span><span>{fmt(doc.timbre)}</span></div>
            )}
            <div className="flex justify-between font-bold text-base border-t pt-2"><span>NET À PAYER</span><span className="text-[#0A3D73]">{fmt(doc.total)}</span></div>
          </div>
        </div>

        {doc.notes && <p className="text-sm text-slate-500 mt-4 italic">Notes: {doc.notes}</p>}

        {(doc.client_signature || doc.pdf_frozen) && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="preview-signature-info">
            <p className="font-semibold flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> {doc.client_signature ? `Signé par ${doc.client_signature.signer_name}` : 'Document finalisé'}</p>
            {doc.client_signature && <p className="text-xs mt-1">Le {new Date(doc.client_signature.signed_at).toLocaleString('fr-FR')} — IP {doc.client_signature.ip}</p>}
            {doc.pdf_sha256 && <p className="text-[10px] mt-1 font-mono break-all text-emerald-800/80">SHA-256 {doc.pdf_sha256}</p>}
          </div>
        )}

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-300">Fermer</Button>
          <Button variant="outline" onClick={() => onDownload(doc._id)} className="border-slate-300" data-testid="preview-download-btn">
            <Download className="w-4 h-4 mr-2" /> Télécharger
          </Button>
          <Button onClick={() => onOpenPdf(doc._id)} className="bg-[#0A3D73] hover:bg-[#082E56] text-white" data-testid="preview-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Imprimer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
